import { Injectable } from "@angular/core";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { catchError, map, switchMap } from "rxjs/operators";
import { Observable, of } from "rxjs";
import { KwikspaceService } from "src/app/services/kwikspace.service";
import {
  CreatePurchaseLineItemConfigFailure,
  CreatePurchaseLineItemConfigSuccess,
  CreatePurchaseLineItemFailure,
  CreatePurchaseLineItemSuccess,
  GetMatchedPurchasePricingFailure,
  GetMatchedPurchasePricingSuccess,
  PurchaseActionTypes,
  GetPurchaseSizeFailure,
  GetPurchaseSizeSuccess,
  GetDrawingLayoutDetailSuccess,
  GetFpProjectCategorySuccess,
  GetFpProjectCategoryFailure,
  GetFpProjectSubCategorySuccess,
  GetFpProjectSubCategoryFailure,
  GetFpProjectSizeSuccess,
  GetFpProjectSizeFailure,
  GetFpProjectExtrasSuccess,
  GetFpProjectExtrasFailure,
  GetFpCategorySizeSuccess,
  GetFpCategorySizeFailure,
  GetFpProjectListsSuccess,
  GetFpProjectListsFailure,
  GetFpProjectPriceListsSuccess,
  GetFpProjectPriceListsFailure,
  UpdatePurchaseLineItemSuccess,
  UpdatePurchaseLineItemFailure,
} from "../actions/purchase.action";

@Injectable()
export class PurchaseEffects {
  public constructor(
    private actions$: Actions,
    private service: KwikspaceService
  ) {}

  @Effect()
  CreatePurchaseLineItem$: Observable<any> = this.actions$.pipe(
    ofType(PurchaseActionTypes.CREATE_PURCHASE_LINE_ITEM),
    switchMap((action: any) => {
      return this.service.createPurchaseLineItems(action.payload).pipe(
        map((res: any) => new CreatePurchaseLineItemSuccess(res)),
        catchError((error) => of(new CreatePurchaseLineItemFailure(error)))
      );
    })
  );

  @Effect()
  CreatePurchaseLineItemConfig$: Observable<any> = this.actions$.pipe(
    ofType(PurchaseActionTypes.CREATE_PURCHASE_LINE_ITEM_CONFIG),
    switchMap((action: any) => {
      return this.service.createPurchaseLineItemConfig(action.payload).pipe(
        map((res: any) => new CreatePurchaseLineItemConfigSuccess(res)),
        catchError((error) =>
          of(new CreatePurchaseLineItemConfigFailure(error))
        )
      );
    })
  );

  @Effect()
  GetPurchaseSize$: Observable<any> = this.actions$.pipe(
    ofType(PurchaseActionTypes.GET_PURCHASE_SIZE),
    switchMap((action: any) => {
      return this.service.getPurchaseSizes().pipe(
        map((res: any) => new GetPurchaseSizeSuccess(res)),
        catchError((error) => of(new GetPurchaseSizeFailure(error)))
      );
    })
  );

  @Effect()
  GetDrawingLayoutDetail$: Observable<any> = this.actions$.pipe(
    ofType(PurchaseActionTypes.GET_DRAWING_LAYOUT_DETAIL),
    switchMap((action: any) => {
      return this.service.getDrawingLayoutDetails().pipe(
        map((res: any) => new GetDrawingLayoutDetailSuccess(res)),
        catchError((error) => of(new GetMatchedPurchasePricingFailure(error)))
      );
    })
  );

  @Effect()
  GetMatchedPricing$: Observable<any> = this.actions$.pipe(
    ofType(PurchaseActionTypes.GET_MATCHED_PRICE_LIST),
    switchMap((action: any) => {
      return this.service.getMatchedPricing(action.payload).pipe(
        map((res: any) => new GetMatchedPurchasePricingSuccess(res)),
        catchError((error) => of(new GetMatchedPurchasePricingFailure(error)))
      );
    })
  );

  @Effect()
  GetFpProjectCategories$: Observable<any> = this.actions$.pipe(
    ofType(PurchaseActionTypes.GET_FP_PROJECT_CATEGORY),
    switchMap((action: any) => {
      return this.service.getFpProjectCategories().pipe(
        map((res: any) => new GetFpProjectCategorySuccess(res)),
        catchError((error) => of(new GetFpProjectCategoryFailure(error)))
      );
    })
  );

  @Effect()
  GetFpProjectSubCategories$: Observable<any> = this.actions$.pipe(
    ofType(PurchaseActionTypes.GET_FP_PROJECT_SUB_CATEGORY),
    switchMap((action: any) => {
      return this.service.getFpProjectSubCategories(action.payload).pipe(
        map((res: any) => new GetFpProjectSubCategorySuccess(res)),
        catchError((error) => of(new GetFpProjectSubCategoryFailure(error)))
      );
    })
  );

  @Effect()
  GetFpProjectSizes$: Observable<any> = this.actions$.pipe(
    ofType(PurchaseActionTypes.GET_FP_PROJECT_SIZE),
    switchMap((action: any) => {
      return this.service.getFpProjectSizes(action.payload).pipe(
        map((res: any) => new GetFpProjectSizeSuccess(res)),
        catchError((error) => of(new GetFpProjectSizeFailure(error)))
      );
    })
  );

  @Effect()
  GetFpProjectExtras$: Observable<any> = this.actions$.pipe(
    ofType(PurchaseActionTypes.GET_FP_PROJECT_EXTRAS),
    switchMap((action: any) => {
      return this.service.getFpProjectExtras().pipe(
        map((res: any) => new GetFpProjectExtrasSuccess(res)),
        catchError((error) => of(new GetFpProjectExtrasFailure(error)))
      );
    })
  );

  @Effect()
  GetFpCategorySize$: Observable<any> = this.actions$.pipe(
    ofType(PurchaseActionTypes.GET_FP_PROJECT_CATEGORY_SIZE),
    switchMap((action: any) => {
      return this.service.getFpProjectCategorySize(action.payload).pipe(
        map((res: any) => new GetFpCategorySizeSuccess(res)),
        catchError((error) => of(new GetFpCategorySizeFailure(error)))
      );
    })
  );

  @Effect()
  GetFpProjectLists$: Observable<any> = this.actions$.pipe(
    ofType(PurchaseActionTypes.GET_FP_PROJECT_LIST),
    switchMap((action: any) => {
      return this.service.getFpProjectList(action.payload).pipe(
        map((res: any) => new GetFpProjectListsSuccess(res)),
        catchError((error) => of(new GetFpProjectListsFailure(error)))
      );
    })
  );

  @Effect()
  GetFpProjectPriceLists$: Observable<any> = this.actions$.pipe(
    ofType(PurchaseActionTypes.GET_FP_PROJECT_PRICE_LIST),
    switchMap((action: any) => {
      return this.service.getFpProjectPriceList(action.payload).pipe(
        map((res: any) => new GetFpProjectPriceListsSuccess(res)),
        catchError((error) => of(new GetFpProjectPriceListsFailure(error)))
      );
    })
  );

  @Effect()
  UpdatePurchaseLineItem$: Observable<any> = this.actions$.pipe(
    ofType(PurchaseActionTypes.UPDATE_PURCHASE_LINE_ITEM),
    switchMap((action: any) => {
      return this.service.updatePurchaseLineItem(action.payload, action.id).pipe(
        map((res: any) => new UpdatePurchaseLineItemSuccess(res)),
        catchError((error) => of(new UpdatePurchaseLineItemFailure(error)))
      );
    })
  );
}
